import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ITEM_PARAM } from '../constants/itemSlideOut.js';
import { formatItemParam, parseItemParam } from '../utils/itemParam.js';

/**
 * The item open in the profile slide-out, read from and written to `?item=`
 * (CT-5715). Opening and closing keep every other param — a QA switch, a filter
 * the page keeps in the URL — and push a history entry, so Back closes.
 */
export function useItemParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(ITEM_PARAM);

  const item = useMemo(() => parseItemParam(raw), [raw]);

  const openItem = useCallback(
    (kind, id) =>
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set(ITEM_PARAM, formatItemParam(kind, id));
        return next;
      }),
    [setSearchParams],
  );

  const closeItem = useCallback(
    () =>
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete(ITEM_PARAM);
        return next;
      }),
    [setSearchParams],
  );

  return useMemo(() => ({ raw, item, openItem, closeItem }), [raw, item, openItem, closeItem]);
}

export default useItemParam;
